/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, useImperativeHandle, useMemo, useRef, useState} from 'react';

interface PayloadEditorProps {
  value?: string;
  data?: {
    txLen?: number;
  };
  eventKey?: string;
  node?: {
    setDataValue?: (colKey: string, newValue: string) => void;
  };
  column?: {
    getColId?: () => string;
  };
}

const DATA_MAX_BYTES = 8;

const sanitizeHex = (value = ''): string => {
  return `${value}`.toUpperCase().replace(/[^0-9A-F]/g, '');
};

const clampTxLen = (value?: number): number => {
  return Math.max(1, Math.min(Number(value || DATA_MAX_BYTES), DATA_MAX_BYTES));
};

const toRawHex = (value = '', txLen = DATA_MAX_BYTES): string => {
  return sanitizeHex((value || '').replace(/-/g, '')).slice(0, txLen * 2);
};

const toFixedRawHex = (value = '', txLen = DATA_MAX_BYTES): string => {
  return toRawHex(value, txLen).padEnd(txLen * 2, '0').slice(0, txLen * 2);
};

const getEmptyRaw = (txLen = DATA_MAX_BYTES): string => {
  return ''.padEnd(txLen * 2, '0');
};

const toDisplay = (raw = '', txLen = DATA_MAX_BYTES): string => {
  const padded = `${raw}`.padEnd(txLen * 2, '0').slice(0, txLen * 2);
  const pairs = padded.match(/.{1,2}/g) || [];
  return pairs.join('-');
};

const displayIndexToRawIndex = (displayIndex: number): number => {
  return Math.max(0, displayIndex - Math.floor(displayIndex / 3));
};

const rawIndexToDisplayIndex = (rawIndex: number): number => {
  return rawIndex + Math.floor(rawIndex / 2);
};

const getNextByteBoundaryRawIndex = (rawIndex: number, txLen: number): number => {
  const maxRawIndex = (txLen * 2) - 1;
  const nextByteStart = (Math.floor(rawIndex / 2) + 1) * 2;
  return Math.min(nextByteStart, maxRawIndex);
};

const isHexKey = (value = ''): boolean => {
  return /^[0-9A-F]$/i.test(value);
};

const replaceRawAtIndex = (currentRaw: string, index: number, value: string): string => {
  if (index < 0 || index >= currentRaw.length) {
    return currentRaw;
  }

  return `${currentRaw.slice(0, index)}${value}${currentRaw.slice(index + 1)}`;
};

/**
 * Provides a fixed-width CAN payload cell editor that enforces byte-level hex input.
 *
 * ```typescript
 * <CanRequestPayloadEditor
 *   value={value}
 *   data={data}
 *   eventKey={eventKey}
 *   node={node}
 *   column={column}
 * />
 * ```
 *
 * @param   value    Current payload display value.
 * @param   data     Row data containing the txLen used to size the payload.
 * @param   eventKey Initial keyboard key that started editing, when present.
 * @param   node     AG Grid row node used to sync edited values.
 * @param   column   AG Grid column metadata used to resolve the target field key.
 * @returns The CAN request payload editor component.
 */
const CanRequestPayloadEditor = forwardRef((props: PayloadEditorProps, ref) => {
  const txLen                                 = clampTxLen(props?.data?.txLen);
  const emptyRaw                              = getEmptyRaw(txLen);
  const initialRaw                            = toFixedRawHex(props?.value || '', txLen);
  const [raw, setRaw]                         = useState<string>(initialRaw);
  const [showPlaceholder, setShowPlaceholder] = useState(initialRaw === emptyRaw);
  const rawRef                                = useRef<string>(initialRaw);
  const inputRef                              = useRef<HTMLInputElement | null>(null);

  const commitRaw = function(nextRaw: string) {
    rawRef.current = nextRaw;
    setRaw(nextRaw);
  };

  const syncNodeValue = function(nextRaw: string) {
    const columnKey = props?.column?.getColId?.() || 'txPayload';
    props?.node?.setDataValue?.(columnKey, toDisplay(nextRaw, txLen));
  };

  const placeholderValue = useMemo(() => toDisplay(emptyRaw, txLen), [emptyRaw, txLen]);
  const displayValue = useMemo(() => {
    if (showPlaceholder) {
      return '';
    }
    return toDisplay(raw, txLen);
  }, [raw, txLen, showPlaceholder]);

  useImperativeHandle(ref, () => ({
    getValue() {
      return toDisplay(rawRef.current, txLen);
    }
  }), [txLen]);

  const setCursorAtRawIndex = function(rawIndex: number) {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    const clamped = Math.max(0, Math.min(rawIndex, (txLen * 2) - 1));
    const displayIndex = rawIndexToDisplayIndex(clamped);
    input.setSelectionRange(displayIndex, displayIndex);
  };

  const onEditorFocus = function() {
    if (!props?.eventKey || !isHexKey(props.eventKey)) {
      if (showPlaceholder) {
        window.setTimeout(() => setCursorAtRawIndex(0), 0);
      }
      return;
    }

    setShowPlaceholder(false);
    const seededRaw = replaceRawAtIndex(rawRef.current, 0, props.eventKey.toUpperCase());
    commitRaw(seededRaw);
    syncNodeValue(seededRaw);
    window.setTimeout(() => setCursorAtRawIndex(1), 0);
  };

  const onEditorChange = function(event: React.ChangeEvent<HTMLInputElement>) {
    const sanitized = toFixedRawHex(event.target.value, txLen);
    setShowPlaceholder(false);
    commitRaw(sanitized);
    syncNodeValue(sanitized);
  };

  const onEditorClick = function(event: React.MouseEvent<HTMLInputElement>) {
    const input        = event.currentTarget;
    const displayIndex = input.selectionStart || 0;
    const rawIndex     = displayIndexToRawIndex(displayIndex);
    window.setTimeout(() => setCursorAtRawIndex(rawIndex), 0);
  };

  const onEditorPaste = function(event: React.ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    const input        = event.currentTarget;
    const displayIndex = input.selectionStart || 0;
    const rawIndex     = displayIndexToRawIndex(displayIndex);
    const pasted       = sanitizeHex(event.clipboardData.getData('text'));

    if (!pasted) {
      return;
    }

    const maxLength    = txLen * 2;
    const nextRawChars = rawRef.current.padEnd(maxLength, '0').split('');
    
    const pastedChars = pasted.slice(0, maxLength - rawIndex).split('');
    pastedChars.forEach((char, offset) => {
      nextRawChars[rawIndex + offset] = char;
    });

    const nextRaw   = nextRawChars.join('').slice(0, maxLength);
    const nextIndex = Math.min(rawIndex + pastedChars.length, maxLength - 1);
    setShowPlaceholder(false);
    commitRaw(nextRaw);
    syncNodeValue(nextRaw);
    window.setTimeout(() => setCursorAtRawIndex(nextIndex), 0);
  };

  const onEditorKeyDown = function(event: React.KeyboardEvent<HTMLInputElement>) {
    const input = event.currentTarget;
    const displayIndex = input.selectionStart || 0;
    const rawIndex = displayIndexToRawIndex(displayIndex);

    if (isHexKey(event.key)) {
      event.preventDefault();

      const nextRaw = replaceRawAtIndex(rawRef.current, rawIndex, event.key.toUpperCase());
      const nextIndex = Math.min(rawIndex + 1, (txLen * 2) - 1);

      setShowPlaceholder(false);
      commitRaw(nextRaw);
      syncNodeValue(nextRaw);
      window.setTimeout(() => setCursorAtRawIndex(nextIndex), 0);
      return;
    }

    if (event.key === ' ' || event.key === '-') {
      event.preventDefault();
      const nextBoundary = getNextByteBoundaryRawIndex(rawIndex, txLen);
      setCursorAtRawIndex(nextBoundary);
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();

      const targetIndex = Math.max(0, rawIndex - 1);
      const nextRaw = replaceRawAtIndex(rawRef.current, targetIndex, '0');

      setShowPlaceholder(false);
      commitRaw(nextRaw);
      syncNodeValue(nextRaw);
      window.setTimeout(() => setCursorAtRawIndex(targetIndex), 0);
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();

      const nextRaw = replaceRawAtIndex(rawRef.current, rawIndex, '0');

      setShowPlaceholder(false);
      commitRaw(nextRaw);
      syncNodeValue(nextRaw);
      window.setTimeout(() => setCursorAtRawIndex(rawIndex), 0);
      return;
    }

    if (event.key === 'ArrowLeft' && displayIndex > 0 && displayIndex % 3 === 0) {
      event.preventDefault();
      setCursorAtRawIndex(Math.max(0, rawIndex - 1));
      return;
    }

    if (event.key === 'ArrowRight' && displayIndex % 3 === 2) {
      event.preventDefault();
      setCursorAtRawIndex(Math.min((txLen * 2) - 1, rawIndex + 1));
    }
  };

  return (
    <input ref={inputRef}
      className='can-request-payload-editor-input'
      value={displayValue}
      placeholder={placeholderValue}
      autoFocus
      onFocus={onEditorFocus}
      onChange={onEditorChange}
      onClick={onEditorClick}
      onPaste={onEditorPaste}
      onKeyDown={onEditorKeyDown}
      style={{
        width           : '100%',
        border          : 0,
        outline         : 0,
        backgroundColor : 'transparent',
        letterSpacing   : '0.5px'
      }}/>
  );
});

CanRequestPayloadEditor.displayName = 'CanRequestPayloadEditor';

export default CanRequestPayloadEditor;
